import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelComplementApi,
  createComplementApi,
  downloadPjsApi,
  fixComplementApi,
  getComplementByIdApi,
  markAsFixedComplementApi,
  searchComplementsApi,
  updateComplementApi,
} from "../apis/complementApi";

export const useCreateComplement = (options = {}) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["create_complement"],
    mutationFn: async (data) => {
      return await createComplementApi(data);
    },
    onSuccess() {
      qClient.invalidateQueries([
        "subscription_details",
        "search-en-process-subscriptions",
      ]);
    },
  });
};

export const useUpdateComplement = (options = {}) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["update_complement"],
    mutationFn: async (payload) => {
      return updateComplementApi(payload);
    },
    onSuccess() {
      qClient.invalidateQueries([
        "subscription_details",
        "search-en-process-subscriptions",
      ]);
    },
  });
};

export const useComplementById = (complementId) => {
  return useQuery({
    queryKey: ["complement_info", complementId],
    queryFn: () => getComplementByIdApi(complementId),
    initialData: {
      typeId: "",
      commentary: "",
    },
    retry: 1,
  });
};

export const useSearchComplements = (
  body = {},
  params = { page: 0, pageSize: 5 }
) => {
  const bodyHash = JSON.stringify(body);
  return useQuery({
    queryKey: ["search_complements", bodyHash, params.page, params.pageSize],
    queryFn: () => searchComplementsApi(body, params),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};

export const useCancelComplement = (onSuccess) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["cancel_complement"],
    mutationFn: async (complementId) => {
      return cancelComplementApi(complementId);
    },
    onSuccess() {
      qClient.invalidateQueries([
        "subscription_details",
        "search-en-process-subscriptions",
      ]);
    },
  });
};

export const useMarkAsFixedComplement = (onSuccess) => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["mark_as_fixed"],
    mutationFn: async (id) => {
      return markAsFixedComplementApi(id);
    },
    onSuccess() {
      qClient.invalidateQueries(["get-subscription-complement"]);
      onSuccess?.();
    },
  });
};

export const useFixComplement = () => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["fix_complement"],
    mutationFn: async (body) => {
      return fixComplementApi(body.id, body.data);
    },
    onSuccess() {
      qClient.invalidateQueries(["get-subscription-complement"]);
    },
  });
};

export const useDownloadPjs = () => {
  const qClient = useQueryClient();

  return useMutation({
    mutationKey: ["download_pjs"],
    mutationFn: async (id) => {
      return downloadPjsApi(id);
    },
    onSuccess({ blob, filename, contentType }) {
      const url = window.URL.createObjectURL(
        new Blob([blob], { type: contentType })
      );
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      qClient.invalidateQueries(["download_pjs"]);
    },
  });
};
